import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    IconButton,
    Box,
    Typography,
    Divider,
    Paper,
    CircularProgress
} from '@mui/material';
import { Close, Print, Download } from '@mui/icons-material';
import html2pdf from 'html2pdf.js';

/**
 * A standardized print preview dialog for reports.
 * Designed to show a high-quality, A4-friendly view of the report data.
 */
const ReportPrintDialog = ({
    open,
    onClose,
    title = 'Report Print Preview',
    reportName,
    companyName = 'FluxERP',
    companyAddress,
    companyPhone,
    companyEmail,
    dateRange,
    asOfDate,
    additionalInfo = [],
    autoDownload = false,
    children
}) => {
    const reportRef = React.useRef(null);
    const [layout, setLayout] = React.useState('a4'); // 'a4' or 'thermal'

    React.useEffect(() => {
        if (open && autoDownload) {
            // Wait for a small delay to ensure the content is fully rendered in the DOM
            const timer = setTimeout(() => {
                handleDownloadPDF();
            }, 300);
            return () => clearTimeout(timer);
        }
        if (!open) {
            setLayout('a4');
        }
    }, [open, autoDownload]);

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPDF = async () => {
        const element = reportRef.current;
        if (!element) return;

        const opt = {
            margin: layout === 'thermal' ? [5, 2, 5, 2] : [10, 10, 10, 10],
            filename: `${reportName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: false, letterRendering: true },
            jsPDF: { 
                unit: 'mm', 
                format: layout === 'thermal' ? [80, 297] : 'a4', 
                orientation: 'portrait' 
            }
        };

        try {
            await html2pdf().set(opt).from(element).save();
            if (autoDownload) {
                // Return focus and clean up
                onClose();
            }
        } catch (error) {
            if (autoDownload) onClose();
        }
    };

    const reportContent = (
        <Box
            id="report-print-content"
            ref={reportRef}
            sx={{
                backgroundColor: '#fff',
                margin: '20px auto',
                padding: layout === 'thermal' ? '10px' : '40px',
                width: layout === 'thermal' ? '80mm' : '210mm',
                minHeight: layout === 'thermal' ? 'auto' : '297mm',
                boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                fontFamily: layout === 'thermal' ? 'monospace' : 'inherit',
                '@media print': {
                    margin: 0,
                    padding: layout === 'thermal' ? '2mm' : '10mm',
                    boxShadow: 'none',
                    width: layout === 'thermal' ? '80mm' : '100%',
                    minHeight: 'auto'
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: layout === 'thermal' ? 'column' : 'row', justifyContent: 'space-between', alignItems: layout === 'thermal' ? 'center' : 'flex-start', mb: 3, textAlign: layout === 'thermal' ? 'center' : 'left' }}>
                <Box sx={{ flex: 1, mb: layout === 'thermal' ? 2 : 0 }}>
                    <Typography variant={layout === 'thermal' ? 'body1' : 'h4'} sx={{ fontWeight: 700, color: '#1a1a1a', letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                        {companyName}
                    </Typography>
                    {companyAddress && (
                        <Typography variant="caption" sx={{ mt: 0.5, color: '#666', display: 'block' }}>{companyAddress}</Typography>
                    )}
                    {(companyPhone || companyEmail) && (
                        <Box sx={{ display: 'flex', flexDirection: layout === 'thermal' ? 'column' : 'row', gap: layout === 'thermal' ? 0 : 2, justifyContent: layout === 'thermal' ? 'center' : 'flex-start' }}>
                            {companyPhone && (
                                <Typography variant="caption" sx={{ color: '#666' }}>Tel: {companyPhone}</Typography>
                            )}
                            {companyEmail && (
                                <Typography variant="caption" sx={{ color: '#666' }}>Email: {companyEmail}</Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ textAlign: layout === 'thermal' ? 'center' : 'right', minWidth: layout === 'thermal' ? '100%' : '180px' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                        Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Typography>
                    {dateRange && (
                        <Typography variant="caption" sx={{ display: 'block' }}>
                            <strong>Period:</strong> {dateRange.start}{dateRange.end ? ` to ${dateRange.end}` : ''}
                        </Typography>
                    )}
                    {asOfDate && (
                        <Typography variant="caption" sx={{ display: 'block' }}><strong>As of:</strong> {asOfDate}</Typography>
                    )}
                    {additionalInfo.map((info, idx) => (
                        <Typography key={idx} variant="caption" sx={{ display: 'block' }}>
                            <strong>{info.label}:</strong> {info.value}
                        </Typography>
                    ))}
                </Box>
            </Box>

            <Divider sx={{ mb: 2, borderColor: '#000', borderWidth: 1, borderStyle: layout === 'thermal' ? 'dashed' : 'solid' }} />

            <Typography variant={layout === 'thermal' ? 'body2' : 'h5'} sx={{ fontWeight: 700, textAlign: 'center', mb: 3, letterSpacing: '1px', textTransform: 'uppercase' }}>
                {reportName}
            </Typography>

            <Box sx={{ mt: 2, fontSize: layout === 'thermal' ? '10px' : 'inherit' }}>
                {children}
            </Box>

            <Box sx={{ mt: 4, pt: 2, borderTop: '2px solid #000', display: 'flex', flexDirection: layout === 'thermal' ? 'column' : 'row', justifyContent: 'space-between', alignItems: layout === 'thermal' ? 'center' : 'flex-end', textAlign: layout === 'thermal' ? 'center' : 'left' }}>
                <Box sx={{ flex: 1, mb: layout === 'thermal' ? 2 : 0 }}>
                    <Typography variant="caption" sx={{ color: '#666', fontStyle: 'italic', display: 'block', mb: 0.5 }}>
                        This is a computer-generated document.
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                        Generated via FluxERP &middot; {new Date().toLocaleString()}
                    </Typography>
                </Box>
                {!layout === 'thermal' && (
                    <Box sx={{ textAlign: 'right', minWidth: '200px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            __________________________
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                            Authorized Signature
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={autoDownload ? undefined : onClose}
            maxWidth={autoDownload ? "sm" : "lg"}
            fullWidth
            hideBackdrop={autoDownload}
            transitionDuration={autoDownload ? 0 : undefined}
            PaperProps={autoDownload ? {
                sx: { 
                    visibility: 'hidden', 
                    opacity: 0, 
                    position: 'fixed', 
                    top: '-10000px', 
                    left: '-10000px' 
                }
            } : {
                sx: {
                    minHeight: '80vh',
                    maxHeight: '90vh',
                    bgcolor: '#f5f5f5',
                    '@media print': {
                        boxShadow: 'none',
                        m: 0,
                        p: 0,
                        maxHeight: 'none',
                        bgcolor: '#fff'
                    }
                }
            }}
            sx={autoDownload ? { pointerEvents: 'none' } : {}}
        >
            {autoDownload ? (
                <Box sx={{ width: layout === 'thermal' ? '80mm' : '210mm' }}>
                    {reportContent}
                </Box>
            ) : (
                <>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', '@media print': { display: 'none' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h6">{title}</Typography>
                            <Box sx={{ display: 'flex', bgcolor: 'grey.100', p: 0.5, borderRadius: 1 }}>
                                <Button 
                                    size="small" 
                                    variant={layout === 'a4' ? 'contained' : 'text'}
                                    onClick={() => setLayout('a4')}
                                    sx={{ minWidth: '80px', borderRadius: 1 }}
                                >
                                    A4
                                </Button>
                                <Button 
                                    size="small" 
                                    variant={layout === 'thermal' ? 'contained' : 'text'}
                                    onClick={() => setLayout('thermal')}
                                    sx={{ minWidth: '80px', borderRadius: 1 }}
                                >
                                    Thermal
                                </Button>
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <Close />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ p: 0, display: 'flex', justifyContent: 'center', overflowX: 'auto', backgroundColor: '#f5f5f5', '@media print': { border: 'none', p: 0, display: 'block', backgroundColor: '#fff' } }}>
                        {reportContent}
                    </DialogContent>

                    <DialogActions sx={{ '@media print': { display: 'none' } }}>
                        <Button onClick={onClose}>Close</Button>
                        <Button variant="outlined" startIcon={<Download />} onClick={handleDownloadPDF}>PDF</Button>
                        <Button variant="contained" startIcon={<Print />} onClick={handlePrint}>Print</Button>
                    </DialogActions>
                </>
            )}

            <style>
                {`
          @media print {
            body * {
              visibility: hidden;
            }
            #report-print-content, #report-print-content * {
              visibility: visible;
            }
            #report-print-content {
              position: absolute;
              left: 0;
              top: 0;
              width: ${layout === 'thermal' ? '80mm' : '100% !important'};
              margin: 0 !important;
              padding: ${layout === 'thermal' ? '2mm' : '0'} !important;
            }
            @page {
              size: ${layout === 'thermal' ? '80mm 297mm' : 'A4'};
              margin: ${layout === 'thermal' ? '0' : '15mm'};
            }
            .MuiTable-root {
              width: 100% !important;
            }
            .no-print {
              display: none !important;
            }
          }
        `}
            </style>
        </Dialog>
    );
};

export default ReportPrintDialog;
